import {
  TransactionGroupDto,
  TransactionGroupSubTxn,
  TxnAccountingClass,
  TxnBookAccountDto,
} from '@/api/transaction-groups';
import {
  ExchangeTransactionTypes,
  StakingInOutTransactionTypes,
  TransactionType,
  TransferTransactionTypes,
} from '../types';
import { uniq } from 'lodash-es';
import { getAssetIdentifier, isSameAsset } from '@/components/Asset';
import { AssetDto } from '@/types/assets';

type ProcessedAssetGroup = NonNullable<TransactionGroupDto['__metadata__']>['processedAssetsGroup'];
type ProcessedAssetGroupType = ProcessedAssetGroup['outgoing'];

export const processTxnGroup = (_txnGroup: TransactionGroupDto): TransactionGroupDto => {
  const _groupBookAccount = _determineTxnGroupBookAccount(_txnGroup);

  const processedIncomingAssets = _processAssetsGroup(
    _txnGroup.transactions,
    _txnGroup.incoming?.assets,
  );
  const processedOutgoingAssets = _processAssetsGroup(
    _txnGroup.transactions,
    _txnGroup.outgoing?.assets,
  );

  const processedAssets: ProcessedAssetGroup = {
    incoming: processedIncomingAssets,
    outgoing: processedOutgoingAssets,
  };

  const groupClass = determineTxnGroupClass(_txnGroup);
  const _uniqueCollectionNames: string[] = [];
  const _uniqueTokenIds: string[] = [];

  _txnGroup.transactions?.forEach((txn) => {
    if (txn.relatedNftCollections) {
      txn.relatedNftCollections.forEach(({ name }) => {
        if (!_uniqueCollectionNames.includes(name)) {
          _uniqueCollectionNames.push(name);
        }
      });
    }
    if (txn.relatedNftTokens) {
      txn.relatedNftTokens.forEach(({ nftTokenId }) => {
        if (!_uniqueTokenIds.includes(nftTokenId)) {
          _uniqueTokenIds.push(nftTokenId);
        }
      });
    }
  });

  return {
    ..._txnGroup,
    __metadata__: {
      // Count unique timestamps
      uniqueTimestampCount: uniq([
        _txnGroup.timestamp,
        ...(_txnGroup.transactions || []).map((_txn) => _txn.timestamp),
      ]).length,
      shouldGLAccountBeEditable: _shouldGLAccountBeEditable({ txn: _txnGroup }),
      bookAccount: _groupBookAccount,
      groupCategorizedBy: _txnGroup?.transactions.find(
        (txn) => txn.bookAccount?.id === _groupBookAccount?.id,
      )?.categorizedBy,
      processedAssetsGroup: processedAssets,
      groupClass,
      uniqueRelatedNftTokens: _uniqueTokenIds,
      uniqueRelatedNftCollections: _uniqueCollectionNames,
    },
    // Sub-txns
    transactions: _txnGroup.transactions?.map((_subTxn) =>
      processSubTxn({ txn: _subTxn, parentTxn: _txnGroup }),
    ),
  };
};

export const processSubTxn = ({
  txn,
  parentTxn,
}: {
  txn: TransactionGroupSubTxn;
  parentTxn: TransactionGroupDto;
}) => {
  return {
    ...txn,
    __metadata__: {
      shouldGLAccountBeEditable: _shouldGLAccountBeEditable({
        txn,
        parentTxn,
      }),
      bookAccount: txn.bookAccount,
    },
  };
};

const _determineTxnGroupBookAccount = (txnGroup: TransactionGroupDto): TxnBookAccountDto | null => {
  if (!Array.isArray(txnGroup.transactions) || txnGroup.transactions.length === 0) {
    return null;
  }

  // If only 1 txn in group, return the group-level bookAccount, or the bookAccount of that txn
  if (txnGroup.transactions.length === 1) {
    return txnGroup.bookAccount || txnGroup.transactions[0].bookAccount || null;
  } else {
    /*
     * Grab the bookAccount of the first non-fee txn found in the group
     */
    const firstNonFeeTxnBookAccount = txnGroup.transactions.find(
      (txn) => txn.label !== TransactionType.Fee && txn.bookAccount?.id !== undefined,
    )?.bookAccount;

    // If all non-fee bookAccounts are the same, return that bookAccount
    if (
      firstNonFeeTxnBookAccount &&
      txnGroup.transactions.every(
        (txn) =>
          txn.bookAccount?.id === firstNonFeeTxnBookAccount?.id &&
          txn.label !== TransactionType.Fee,
      )
    ) {
      return firstNonFeeTxnBookAccount;
    } else if (txnGroup.bookAccount) {
      // If non-fee bookAccounts are different, return the group-level bookAccount
      return txnGroup.bookAccount;
    }
  }

  return null;
};

export const determineTxnGroupClass = (
  txnGroup: TransactionGroupDto,
): TxnAccountingClass | null => {
  if (!Array.isArray(txnGroup.transactions) || txnGroup.transactions.length === 0) {
    return null;
  }

  // If only 1 txn in group, return the group-level accountingClass, or the accountingClass of that txn
  if (txnGroup.transactions.length === 1) {
    return txnGroup.accountingClass || txnGroup.transactions[0].accountingClass || null;
  } else {
    /*
     * Grab the accountingClass of the first non-fee txn found in the group
     */
    const firstNonFeeTxnAccountingClass = txnGroup.transactions.find(
      (txn) => txn.label !== TransactionType.Fee && txn.accountingClass?.id !== undefined,
    )?.accountingClass;

    // If all non-fee accountingClasses are the same, return that accountingClass
    if (
      firstNonFeeTxnAccountingClass &&
      txnGroup.transactions.every(
        (txn) =>
          txn.accountingClass?.id === firstNonFeeTxnAccountingClass?.id &&
          txn.label !== TransactionType.Fee,
      )
    ) {
      return firstNonFeeTxnAccountingClass;
    } else if (txnGroup.accountingClass) {
      // If non-fee accountingClasses are different, return the group-level accountingClass
      return txnGroup.accountingClass;
    }
  }

  return null;
};

const _shouldGLAccountBeEditable = ({
  txn,
  parentTxn,
}: {
  txn: TransactionGroupDto | TransactionGroupSubTxn;
  parentTxn?: TransactionGroupDto;
}) => {
  const isTransfer = TransferTransactionTypes.includes(txn.label);
  const isExchange = ExchangeTransactionTypes.includes(txn.label);
  const isStakingInOut = StakingInOutTransactionTypes.includes(txn.label);

  const _shouldBeEditable =
    (!parentTxn ? (txn as TransactionGroupDto).tab !== 'synced' : parentTxn?.tab !== 'synced') && // Only editable if tab is NOT `synced`
    !isTransfer &&
    !isExchange &&
    !isStakingInOut &&
    !!(
      parentTxn ||
      (txn as TransactionGroupDto).transactions.length === 1 ||
      (txn as TransactionGroupDto).transactions.some(
        (txn) =>
          !TransferTransactionTypes.includes(txn.label) &&
          !ExchangeTransactionTypes.includes(txn.label) &&
          !StakingInOutTransactionTypes.includes(txn.label) &&
          txn.label !== TransactionType.Fee,
      )
    );

  return _shouldBeEditable;
};

const _processAssetsGroup = (
  transactions: TransactionGroupDto['transactions'],
  assets?: AssetDto[],
) => {
  const groupedAssets = (assets || []).reduce<ProcessedAssetGroupType>((prev, curr) => {
    const identifier = getAssetIdentifier(curr);
    if (!identifier) return prev;

    const group = !prev[identifier] ? [curr] : [...prev[identifier].assets, curr];
    const nftGroup = !prev[identifier] ? [] : [...prev[identifier].nfts];

    if (curr.type === 'nft') nftGroup.push(curr);

    const isMissingPrice = transactions.some(
      (txn) => isSameAsset(group[0], txn.asset) && (txn.fiat === null || txn.fiat === undefined),
    );

    return { ...prev, [identifier]: { assets: group, isMissingPrice, nfts: nftGroup } };
  }, {});

  return groupedAssets;
};
